import { apiFetch } from './client.js';

export interface CustomDb {
  id: string;
  name: string;
  description: string | null;
  source: string | null;
  kind: 'csv' | 'json' | 'sqlite' | 'xlsx' | null;
  mimeType: string | null;
  sizeBytes: number | null;
  rowCount: number;
  parseStatus: 'pending' | 'parsing' | 'ready' | 'failed';
  parseError: string | null;
  enabled: boolean;
  schema: any;
  preview: any;
  parsedAt: string | null;
  updatedAt: string;
  createdAt: string;
  uploadedBy: string | null;
}

export interface CustomDbStats {
  total: number;
  totalRows: number;
  byStatus: Record<string, number>;
  byKind: Record<string, number>;
  enabled: number;
}

export interface CustomDbSearchHit {
  dbId: string;
  dbName: string;
  tableName: string | null;
  rowIdx: number;
  payload: Record<string, unknown>;
  score: number;
  match: 'fts' | 'trgm';
}

export interface CustomDbSearchResult {
  query: string;
  total: number;
  durationMs: number;
  strategy: 'fts' | 'trgm' | 'mixed' | 'none';
  hits: CustomDbSearchHit[];
}

export async function listCustomDbs(): Promise<CustomDb[]> {
  const r = await apiFetch<{ total: number; dbs: CustomDb[] }>('/api/v1/custom-dbs');
  return r.dbs;
}

export async function getCustomDb(id: string): Promise<CustomDb> {
  return await apiFetch<CustomDb>(`/api/v1/custom-dbs/${id}`);
}

export async function getCustomDbStats(): Promise<CustomDbStats> {
  return await apiFetch<CustomDbStats>('/api/v1/custom-dbs/stats');
}

export async function uploadCustomDb(input: {
  file: File;
  name?: string;
  description?: string;
  source?: string;
}): Promise<CustomDb> {
  const fd = new FormData();
  fd.append('file', input.file, input.file.name);
  if (input.name) fd.append('name', input.name);
  if (input.description) fd.append('description', input.description);
  if (input.source) fd.append('source', input.source);
  const res = await fetch('/api/v1/custom-dbs', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(`upload ${res.status}: ${txt.slice(0, 200)}`);
  }
  return (await res.json()) as CustomDb;
}

export async function updateCustomDb(
  id: string,
  patch: { name?: string; description?: string | null; enabled?: boolean },
): Promise<CustomDb> {
  return await apiFetch<CustomDb>(`/api/v1/custom-dbs/${id}`, {
    method: 'PATCH',
    body: patch,
  });
}

export async function deleteCustomDb(id: string): Promise<void> {
  await apiFetch(`/api/v1/custom-dbs/${id}`, { method: 'DELETE' });
}

export async function reparseCustomDb(id: string): Promise<{ jobId: string }> {
  return await apiFetch<{ ok: true; jobId: string }>(`/api/v1/custom-dbs/${id}/reparse`, {
    method: 'POST',
  });
}

export async function searchCustomDbs(opts: {
  q: string;
  dbId?: string;
  limit?: number;
}): Promise<CustomDbSearchResult> {
  const qs = new URLSearchParams({ q: opts.q });
  if (opts.dbId) qs.set('dbId', opts.dbId);
  if (opts.limit) qs.set('limit', String(opts.limit));
  return await apiFetch<CustomDbSearchResult>(`/api/v1/custom-dbs/search?${qs.toString()}`);
}

export async function listCustomDbRows(
  id: string,
  opts: { limit?: number; offset?: number; tableName?: string } = {},
): Promise<{ total: number; rows: Array<{ rowIdx: number; tableName: string | null; payload: any }> }> {
  const qs = new URLSearchParams();
  if (opts.limit != null) qs.set('limit', String(opts.limit));
  if (opts.offset != null) qs.set('offset', String(opts.offset));
  if (opts.tableName) qs.set('tableName', opts.tableName);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return await apiFetch(`/api/v1/custom-dbs/${id}/rows${suffix}`);
}
